import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Card,
  Form,
  InputNumber,
  Select,
  DatePicker,
  Input,
  Button,
  Typography,
  Table,
  Statistic,
  Row,
  Col,
  Empty,
  Spin,
  message,
  Tag,
  Result,
  Modal,
  Popconfirm,
  Space
} from 'antd'
import {
  DollarCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'
import Hint from '../components/common/Hint'
import { now as tzNow, fromPicker } from '../utils/timezone'

const { Title, Paragraph, Text } = Typography

const REMARKS_MAX_LENGTH = 500
const REMARKS_TRUNCATE_LEN = 25

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 32px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledCard = styled(Card)`
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(213, 114, 138, 0.12);
  border: 1px solid rgba(213, 114, 138, 0.08);

  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const FormSection = styled(Card)`
  border-radius: 18px;
  border: 1px solid rgba(213, 114, 138, 0.08);
  box-shadow: 0 12px 28px rgba(213, 114, 138, 0.1);
  background: linear-gradient(135deg, #fff7fa 0%, #ffffff 100%);

  .ant-card-head {
    border-bottom: none;
  }
`

const TableWrapper = styled(Card)`
  border-radius: 18px;
  border: 1px solid rgba(15, 37, 98, 0.06);
  box-shadow: 0 12px 28px rgba(15, 37, 98, 0.08);
`

const ResponsiveTable = styled(Table)`
  @media (max-width: 768px) {
    display: none;
  }
`

const MobileCardList = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    margin-top: 16px;
    border-top: 1px solid #f3f4f6;
    padding-top: 16px;
  }
`

const TransactionCard = styled(Card)`
  margin-bottom: 8px;
  border-radius: 12px;
  border: 1px solid rgba(15, 37, 98, 0.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  .ant-card-body {
    padding: 12px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`

const CardLabel = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`

const CardValue = styled.span`
  font-size: 14px;
  color: #1a1a1a;
  font-weight: ${(props) => (props.bold ? 600 : 400)};
  text-align: right;
  flex: 1;
  margin-left: 12px;
`

const CardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f3f4f6;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`

const SummaryRow = styled(Row)`
  margin-top: 8px;
`

const AmountText = styled.span`
  font-weight: 600;
  color: ${(props) => (props.negative ? '#cf1322' : '#0f2562')};
`

const StatusTag = ({ type }) => {
  const tagMap = {
    income: { color: 'green', text: '入帳' },
    expense: { color: 'volcano', text: '支出' },
    adjustment: { color: 'blue', text: '調整' }
  }
  const config = tagMap[type] || tagMap.adjustment
  return <Tag color={config.color}>{config.text}</Tag>
}

const formatCurrency = (value) => {
  const numeric = Number(value) || 0
  return `NT$ ${numeric.toLocaleString()}`
}

const normalizeTransactions = (payload) => {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.transactions)) return payload.transactions
  if (Array.isArray(payload?.records)) return payload.records
  if (Array.isArray(payload?.items)) return payload.items
  return []
}

const AllowanceSettingsPage = () => {
  const { user, businessPlan } = useAuth()
  const navigate = useNavigate()
  const { hasFeature, permissionsLoaded } = useMembership()
  const { can } = usePermission()
  const [form] = Form.useForm()
  const [editForm] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [transactions, setTransactions] = useState([])
  const [balance, setBalance] = useState(0)
  const [monthlyBalances, setMonthlyBalances] = useState([])
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const [editing, setEditing] = useState(false)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [detailModalContent, setDetailModalContent] = useState('')
  const [selectedMonth, setSelectedMonth] = useState(null) // 月份篩選，預設 null = 全部

  const businessId = user?.businessId
  const { permissions, businessRole } = usePermission()
  const canViewFinance = can('finance:view')

  const fetchAllowance = useCallback(async () => {
    if (!businessId) return
    try {
      setLoading(true)
      const response = await api.get(
        `/api/businesses/${businessId}/commission/allowance`
      )
      const payload =
        response.data?.data || response.data?.allowance || response.data || {}

      const balanceValue =
        Number(
          payload.balance ?? payload.totalBalance ?? payload.remain ?? 0
        ) || 0
      const list = normalizeTransactions(payload)
      const monthlyList = Array.isArray(payload.monthlyBalances)
        ? payload.monthlyBalances
        : []

      setBalance(balanceValue)
      setMonthlyBalances(monthlyList)
      setTransactions(
        list.map((item, index) => ({
          id: item._id || item.id || index,
          date:
            item.date ||
            item.createdAt ||
            item.time ||
            item.timestamp ||
            tzNow().toISOString(),
          description: item.description || item.note || item.memo || '',
          type:
            item.type ||
            (Number(item.amount) >= 0 ? 'income' : 'expense') ||
            'adjustment',
          amount: Number(item.amount ?? item.value ?? 0) || 0,
          balanceAfter:
            Number(item.balanceAfter ?? item.remaining ?? item.balance ?? 0) ||
            null
        }))
      )
    } catch (error) {
      console.error('取得零用金設定失敗', error)
    } finally {
      setLoading(false)
    }
  }, [businessId])

  useEffect(() => {
    if (!hasFeature('salary')) return
    fetchAllowance()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, hasFeature])

  // 計算當月餘額
  const currentMonthBalance = useMemo(() => {
    const currentMonth = tzNow().format('YYYY-MM')
    const monthData = monthlyBalances.find((m) => m.month === currentMonth)
    return monthData?.balance ?? null
  }, [monthlyBalances])

  // 依選取月份篩選紀錄
  const filteredTransactions = useMemo(() => {
    if (!selectedMonth) return transactions
    const month = selectedMonth.format('YYYY-MM')
    return transactions.filter(
      (t) => t.date && dayjs(t.date).format('YYYY-MM') === month
    )
  }, [transactions, selectedMonth])

  // 所有 Hooks 必須在權限檢查之前
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: '日期',
        dataIndex: 'date',
        key: 'date',
        render: (value) =>
          value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '--',
        width: 160
      },
      {
        title: '類型',
        dataIndex: 'type',
        key: 'type',
        width: 80,
        render: (value) => <StatusTag type={value} />
      },
      {
        title: '金額',
        dataIndex: 'amount',
        key: 'amount',
        width: 140,
        align: 'right',
        render: (value) => (
          <AmountText negative={Number(value) < 0}>
            {formatCurrency(value)}
          </AmountText>
        )
      }
    ]

    // 如果有 finance:view 权限，显示余额列
    if (canViewFinance) {
      baseColumns.push({
        title: '餘額',
        dataIndex: 'balanceAfter',
        key: 'balanceAfter',
        width: 160,
        align: 'right',
        render: (value) =>
          value !== null ? (
            formatCurrency(value)
          ) : (
            <Text type='secondary'>--</Text>
          )
      })
    }

    baseColumns.push({
      title: '明細',
      dataIndex: 'description',
      key: 'description',
      ellipsis: false,
      render: (value) => {
        if (!value) return <Text type='secondary'>無備註</Text>
        const str = String(value)
        const truncated = str.length > REMARKS_TRUNCATE_LEN
        return (
          <span>
            {truncated ? `${str.slice(0, REMARKS_TRUNCATE_LEN)}...` : str}
            {truncated && (
              <Button
                type='link'
                size='small'
                style={{ padding: '0 4px', height: 'auto' }}
                onClick={() => {
                  setDetailModalContent(str)
                  setDetailModalVisible(true)
                }}
              >
                查看更多
              </Button>
            )}
          </span>
        )
      }
    })

    // 添加操作欄
    baseColumns.push({
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Space size='small'>
          <Button
            type='link'
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size='small'
            title='編輯'
          />
          <Popconfirm
            title='確定要刪除此零用金記錄嗎？'
            description='刪除後將無法恢復，請確認此操作。'
            onConfirm={() => handleDelete(record)}
            okText='確定'
            cancelText='取消'
            okButtonProps={{ danger: true }}
          >
            <Button
              type='link'
              danger
              icon={<DeleteOutlined />}
              size='small'
              title='刪除'
            />
          </Popconfirm>
        </Space>
      )
    })

    return baseColumns
  }, [canViewFinance])

  // 權限檢查：只有品牌方案才能使用此功能
  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size='large' tip='正在載入權限...' />
        </div>
      </PageWrapper>
    )
  }

  if (!hasFeature('salary')) {
    return (
      <PageWrapper>
        <ContentWrapper>
          <Result
            status='403'
            title='功能限制'
            subTitle='此功能需要品牌方案才能使用，請升級您的會員方案。'
            extra={
              <Button
                type='primary'
                onClick={() => navigate('/settings/membership')}
              >
                查看會員方案
              </Button>
            }
          />
        </ContentWrapper>
      </PageWrapper>
    )
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (!businessId) {
        message.error('未能取得商家資訊，請重新登入後再試')
        return
      }

      setSaving(true)

      // 計算金額
      let amount = Number(values.amount)
      if (values.direction === 'expense') {
        // 支出：強制為負數
        amount = -Math.abs(amount)
      } else if (values.direction === 'adjustment') {
        // 調整：保留用戶輸入的正負號（正數=增加，負數=減少）
        amount = Number(values.amount)
      } else {
        // 入帳：強制為正數
        amount = Math.abs(amount)
      }

      await api.post(`/api/businesses/${businessId}/commission/allowance`, {
        amount: amount,
        type: values.direction,
        description: values.description?.trim() || '',
        // fromPicker：使用者挑 9/15 就是 9/15。直接 toISOString() 會送出
        // 9/14T17:00Z，後端依台灣日期分桶時會落到前一天。
        date: values.date
          ? fromPicker(values.date).toISOString()
          : tzNow().toISOString()
      })

      message.success('零用金紀錄已新增')
      form.resetFields()
      form.setFieldsValue({
        direction: 'expense',
        date: tzNow()
      })
      fetchAllowance()
    } catch (error) {
      if (error?.errorFields) {
        return
      }
      console.error('新增零用金紀錄失敗', error)
      message.error(
        error.response?.data?.message || error.message || '新增失敗，請稍後再試'
      )
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    form.resetFields()
  }

  // 打開編輯 Modal
  const handleEdit = (record) => {
    setEditingRecord(record)
    editForm.setFieldsValue({
      type: record.type,
      amount: record.amount, // 保留原始金額（包含正負號）
      date: record.date ? dayjs(record.date) : tzNow(),
      description: record.description || ''
    })
    setEditModalVisible(true)
  }

  // 提交編輯
  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields()
      if (!businessId || !editingRecord) {
        message.error('未能取得商家資訊，請重新登入後再試')
        return
      }

      setEditing(true)

      // 計算金額
      let amount = Number(values.amount)
      if (values.type === 'expense') {
        // 支出：強制為負數
        amount = -Math.abs(amount)
      } else if (values.type === 'adjustment') {
        // 調整：保留用戶輸入的正負號（正數=增加，負數=減少）
        amount = Number(values.amount)
      } else {
        // 入帳：強制為正數
        amount = Math.abs(amount)
      }

      await api.put(
        `/api/businesses/${businessId}/commission/allowance/${editingRecord.id}`,
        {
          type: values.type,
          amount: amount,
          description: values.description?.trim() || '',
          date: values.date
            ? fromPicker(values.date).toISOString()
            : tzNow().toISOString()
        }
      )

      message.success('零用金紀錄已更新')
      setEditModalVisible(false)
      editForm.resetFields()
      setEditingRecord(null)
      fetchAllowance()
    } catch (error) {
      if (error?.errorFields) {
        return
      }
      console.error('更新零用金紀錄失敗', error)
      message.error(
        error.response?.data?.message || error.message || '更新失敗，請稍後再試'
      )
    } finally {
      setEditing(false)
    }
  }

  // 刪除零用金記錄
  const handleDelete = async (record) => {
    try {
      if (!businessId) {
        message.error('未能取得商家資訊，請重新登入後再試')
        return
      }

      setLoading(true)
      await api.delete(
        `/api/businesses/${businessId}/commission/allowance/${record.id}`
      )
      message.success('零用金紀錄已刪除')
      fetchAllowance()
    } catch (error) {
      console.error('刪除零用金紀錄失敗', error)
      message.error(
        error.response?.data?.message || error.message || '刪除失敗，請稍後再試'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <StyledCard>
          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <Title level={3} style={{ marginBottom: 8, color: '#0f2562' }}>
                零用金設定
              </Title>
              <Paragraph style={{ color: '#576079', marginBottom: 0 }}>
                追蹤零用金的入帳與支出，掌握剩餘餘額與每筆明細，確保帳務透明。
              </Paragraph>
            </Col>
            {canViewFinance && (
              <>
                <Col xs={24} md={12}>
                  <Card
                    bordered={false}
                    style={{
                      borderRadius: 16,
                      background:
                        'linear-gradient(135deg, rgba(213,114,138,0.16), rgba(255,255,255,0.36))',
                      height: '100%'
                    }}
                  >
                    <Statistic
                      title='目前零用金總餘額'
                      valueStyle={{
                        color: balance < 0 ? '#cf1322' : '#0f2562'
                      }}
                      value={formatCurrency(balance)}
                      prefix={
                        <DollarCircleOutlined style={{ color: '#d5728a' }} />
                      }
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card
                    bordered={false}
                    style={{
                      borderRadius: 16,
                      background:
                        'linear-gradient(135deg, rgba(15,37,98,0.08), rgba(255,255,255,0.36))',
                      height: '100%'
                    }}
                  >
                    <Statistic
                      title={`${tzNow().format('YYYY年MM月')}餘額`}
                      valueStyle={{
                        color:
                          currentMonthBalance !== null &&
                          currentMonthBalance < 0
                            ? '#cf1322'
                            : '#0f2562'
                      }}
                      value={
                        currentMonthBalance !== null
                          ? formatCurrency(currentMonthBalance)
                          : '--'
                      }
                      prefix={
                        <DollarCircleOutlined style={{ color: '#0f2562' }} />
                      }
                    />
                  </Card>
                </Col>
              </>
            )}
          </Row>
        </StyledCard>

        <FormSection
          title='新增零用金紀錄'
          extra={
            <Tag color='processing' style={{ borderRadius: 999 }}>
              保持帳務更新
            </Tag>
          }
        >
          <Form
            layout='vertical'
            form={form}
            initialValues={{
              direction: 'expense',
              amount: undefined,
              date: tzNow()
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={6}>
                <Form.Item
                  name='direction'
                  label='交易類型'
                  rules={[{ required: true, message: '請選擇交易類型' }]}
                >
                  <Select size='large'>
                    <Select.Option value='income'>入帳</Select.Option>
                    <Select.Option value='expense'>支出</Select.Option>
                    <Select.Option value='adjustment'>餘額調整</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              {/* 選擇「入帳」時顯示全寬提示條，避免客戶付款誤存零用金導致報表漏收 */}
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.direction !== cur.direction}>
                {({ getFieldValue }) =>
                  getFieldValue('direction') === 'income' ? (
                    <Col xs={24}>
                      <Hint
                        tone='warning'
                        style={{ borderRadius: 8, fontSize: 13, marginTop: -8 }}
                        title='入帳僅供老闆自行撥款補充零用金 — 客戶付款請走「結帳」流程，勿存入零用金，否則將造成營收報表數字錯誤。'
                      />
                    </Col>
                  ) : null
                }
              </Form.Item>
              <Col xs={24} md={6}>
                <Form.Item
                  name='amount'
                  label='金額'
                  rules={[
                    { required: true, message: '請輸入金額' },
                    {
                      type: 'number',
                      validator: (_, value) => {
                        if (value === undefined || value === null) {
                          return Promise.reject(new Error('請輸入金額'))
                        }
                        const formValues = form.getFieldsValue()
                        // 如果是餘額調整，允許負數；否則只允許正數
                        if (formValues.direction === 'adjustment') {
                          return Promise.resolve()
                        }
                        if (Number(value) < 0) {
                          return Promise.reject(new Error('金額不得小於 0'))
                        }
                        return Promise.resolve()
                      }
                    }
                  ]}
                >
                  <InputNumber
                    size='large'
                    min={
                      form.getFieldValue('direction') === 'adjustment'
                        ? undefined
                        : 0
                    }
                    style={{ width: '100%' }}
                    placeholder={
                      form.getFieldValue('direction') === 'adjustment'
                        ? '正數=增加，負數=減少'
                        : '輸入金額'
                    }
                    formatter={(value) => {
                      if (value === undefined || value === null) return ''
                      const numeric = Number(value)
                      if (Number.isNaN(numeric)) return ''
                      return numeric
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }}
                    parser={(value) => {
                      if (!value) return 0
                      // 允許負數輸入
                      const cleaned = value.replace(/[^\d.-]/g, '')
                      return cleaned ? Number(cleaned) : 0
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name='date'
                  label='日期'
                  rules={[{ required: true, message: '請選擇日期' }]}
                >
                  <DatePicker
                    size='large'
                    style={{ width: '100%' }}
                    format='YYYY-MM-DD'
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name='description'
                  label='明細備註'
                  rules={[
                    {
                      max: REMARKS_MAX_LENGTH,
                      message: `備註不可超過 ${REMARKS_MAX_LENGTH} 字`
                    }
                  ]}
                >
                  <Input.TextArea
                    size='large'
                    placeholder='輸入用途或備註'
                    allowClear
                    rows={4}
                    maxLength={REMARKS_MAX_LENGTH}
                    showCount
                  />
                </Form.Item>
              </Col>
            </Row>

            <Actions>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                清除
              </Button>
              <Button
                type='primary'
                icon={<PlusOutlined />}
                onClick={handleSubmit}
                loading={saving}
              >
                新增紀錄
              </Button>
            </Actions>
          </Form>
        </FormSection>

        <TableWrapper
          title='零用金紀錄'
          extra={
            <Space>
              <DatePicker
                picker='month'
                value={selectedMonth}
                onChange={(val) => setSelectedMonth(val)}
                allowClear
                placeholder='全部月份'
                format='YYYY年MM月'
                style={{ borderRadius: 8 }}
              />
              <Tag color='magenta' style={{ borderRadius: 999 }}>
                最近更新：{tzNow().format('YYYY/MM/DD HH:mm')}
              </Tag>
            </Space>
          }
        >
          {loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px 0'
              }}
            >
              <Spin size='large' />
            </div>
          ) : transactions.length === 0 ? (
            <Empty description='尚無零用金紀錄，快來新增第一筆吧！' />
          ) : (
            <>
              {canViewFinance && (
                <SummaryRow gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Statistic
                      title={`${selectedMonth ? selectedMonth.format('YYYY年MM月') + '' : '全部'}入帳金額`}
                      value={formatCurrency(
                        filteredTransactions
                          .filter((item) => Number(item.amount) > 0)
                          .reduce((sum, item) => sum + Number(item.amount), 0)
                      )}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <Statistic
                      title={`${selectedMonth ? selectedMonth.format('YYYY年MM月') + '' : '全部'}支出金額`}
                      value={formatCurrency(
                        filteredTransactions
                          .filter((item) => Number(item.amount) < 0)
                          .reduce((sum, item) => sum + Number(item.amount), 0)
                      )}
                      valueStyle={{ color: '#cf1322' }}
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <Statistic title='篩選筆數' value={filteredTransactions.length} />
                  </Col>
                </SummaryRow>
              )}

              <ResponsiveTable
                rowKey='id'
                columns={columns}
                dataSource={filteredTransactions}
                pagination={{ pageSize: 10 }}
                style={{ marginTop: canViewFinance ? 24 : 0 }}
              />

              <MobileCardList>
                {filteredTransactions.map((transaction) => (
                  <TransactionCard key={transaction.id}>
                    <CardRow>
                      <CardLabel>日期</CardLabel>
                      <CardValue>
                        {transaction.date
                          ? dayjs(transaction.date).format('YYYY-MM-DD HH:mm')
                          : '--'}
                      </CardValue>
                    </CardRow>

                    <CardRow>
                      <CardLabel>類型</CardLabel>
                      <CardValue>
                        <StatusTag type={transaction.type} />
                      </CardValue>
                    </CardRow>

                    <CardRow>
                      <CardLabel>金額</CardLabel>
                      <CardValue bold>
                        <AmountText negative={Number(transaction.amount) < 0}>
                          {formatCurrency(transaction.amount)}
                        </AmountText>
                      </CardValue>
                    </CardRow>

                    {canViewFinance && transaction.balanceAfter !== null && (
                      <CardRow>
                        <CardLabel>餘額</CardLabel>
                        <CardValue>
                          {formatCurrency(transaction.balanceAfter)}
                        </CardValue>
                      </CardRow>
                    )}

                    <CardRow>
                      <CardLabel>明細</CardLabel>
                      <CardValue>
                        {transaction.description ? (
                          (() => {
                            const str = String(transaction.description)
                            const truncated = str.length > REMARKS_TRUNCATE_LEN
                            return (
                              <span>
                                {truncated
                                  ? `${str.slice(0, REMARKS_TRUNCATE_LEN)}...`
                                  : str}
                                {truncated && (
                                  <Button
                                    type='link'
                                    size='small'
                                    style={{ padding: '0 4px', height: 'auto' }}
                                    onClick={() => {
                                      setDetailModalContent(str)
                                      setDetailModalVisible(true)
                                    }}
                                  >
                                    查看更多
                                  </Button>
                                )}
                              </span>
                            )
                          })()
                        ) : (
                          <Text type='secondary'>無備註</Text>
                        )}
                      </CardValue>
                    </CardRow>

                    <CardActions>
                      <Button
                        type='link'
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(transaction)}
                        size='small'
                      >
                        編輯
                      </Button>
                      <Popconfirm
                        title='確定要刪除此零用金記錄嗎？'
                        description='刪除後將無法恢復，請確認此操作。'
                        onConfirm={() => handleDelete(transaction)}
                        okText='確定'
                        cancelText='取消'
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          type='link'
                          danger
                          icon={<DeleteOutlined />}
                          size='small'
                        >
                          刪除
                        </Button>
                      </Popconfirm>
                    </CardActions>
                  </TransactionCard>
                ))}
              </MobileCardList>
            </>
          )}
        </TableWrapper>

        {/* 編輯零用金記錄 Modal */}
        <Modal
          title='編輯零用金記錄'
          open={editModalVisible}
          onOk={handleEditSubmit}
          onCancel={() => {
            setEditModalVisible(false)
            editForm.resetFields()
            setEditingRecord(null)
          }}
          confirmLoading={editing}
          okText='確認修改'
          cancelText='取消'
          width={600}
        >
          <Form form={editForm} layout='vertical'>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name='type'
                  label='交易類型'
                  rules={[{ required: true, message: '請選擇交易類型' }]}
                >
                  <Select size='large'>
                    <Select.Option value='income'>入帳</Select.Option>
                    <Select.Option value='expense'>支出</Select.Option>
                    <Select.Option value='adjustment'>餘額調整</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name='amount'
                  label='金額'
                  rules={[
                    { required: true, message: '請輸入金額' },
                    {
                      type: 'number',
                      validator: (_, value) => {
                        if (value === undefined || value === null) {
                          return Promise.reject(new Error('請輸入金額'))
                        }
                        const formValues = editForm.getFieldsValue()
                        // 如果是餘額調整，允許負數；否則只允許正數
                        if (formValues.type === 'adjustment') {
                          return Promise.resolve()
                        }
                        if (Number(value) < 0) {
                          return Promise.reject(new Error('金額不得小於 0'))
                        }
                        return Promise.resolve()
                      }
                    }
                  ]}
                >
                  <InputNumber
                    size='large'
                    min={
                      editForm.getFieldValue('type') === 'adjustment'
                        ? undefined
                        : 0
                    }
                    style={{ width: '100%' }}
                    placeholder={
                      editForm.getFieldValue('type') === 'adjustment'
                        ? '正數=增加，負數=減少'
                        : '輸入金額'
                    }
                    formatter={(value) => {
                      if (value === undefined || value === null) return ''
                      const numeric = Number(value)
                      if (Number.isNaN(numeric)) return ''
                      return numeric
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }}
                    parser={(value) => {
                      if (!value) return 0
                      // 允許負數輸入
                      const cleaned = value.replace(/[^\d.-]/g, '')
                      return cleaned ? Number(cleaned) : 0
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name='date'
                  label='日期'
                  rules={[{ required: true, message: '請選擇日期' }]}
                >
                  <DatePicker
                    size='large'
                    style={{ width: '100%' }}
                    format='YYYY-MM-DD'
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name='description'
                  label='明細備註'
                  rules={[
                    {
                      max: REMARKS_MAX_LENGTH,
                      message: `備註不可超過 ${REMARKS_MAX_LENGTH} 字`
                    }
                  ]}
                >
                  <Input.TextArea
                    size='large'
                    placeholder='輸入用途或備註'
                    allowClear
                    rows={4}
                    maxLength={REMARKS_MAX_LENGTH}
                    showCount
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* 明細全文 Modal */}
        <Modal
          title='明細'
          open={detailModalVisible}
          onCancel={() => {
            setDetailModalVisible(false)
            setDetailModalContent('')
          }}
          footer={[
            <Button
              key='close'
              type='primary'
              onClick={() => {
                setDetailModalVisible(false)
                setDetailModalContent('')
              }}
            >
              關閉
            </Button>
          ]}
          width={520}
        >
          <div
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '60vh',
              overflowY: 'auto',
              padding: '8px 0'
            }}
          >
            {detailModalContent || '無備註'}
          </div>
        </Modal>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default AllowanceSettingsPage
